/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useMutation } from '@apollo/client';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { isNil } from 'es-toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { usePaymentCards } from '@fai/friendli-suite/features/settings/team-settings/use-payment';
import {
  TeamCheckStripeSetupIntentCompletionDocument,
  TeamPaymentCardsDocument,
  TeamPaymentDocument,
  TeamCreateStripeIntentDocument,
  CreateStripeCustomerDocument,
  ClientTeamStripeSetupIntentState,
} from '@fai/friendli-suite/generated/client/graphql';
import { ampTrack } from '@fai/friendli-suite/shared/amplitude';
import { successProps } from '@fai/ui/components/notification/props';

export const useStripeClientSecret = () => {
  const { teamId = '' } = useParams();

  const [stripeClientSecret, setStripeClientSecret] = React.useState<string | null>(null);

  const [createStripeIntent] = useMutation(TeamCreateStripeIntentDocument, {
    variables: { input: { teamId } },
  });
  const [createStripeCustomer] = useMutation(CreateStripeCustomerDocument, {
    variables: { input: { teamId } },
  });

  React.useEffect(() => {
    (async () => {
      await createStripeCustomer();
      await createStripeIntent().then((res) => {
        setStripeClientSecret(res.data?.clientTeamCreateStripeIntent?.clientSecret ?? null);
      });
    })();
  }, [createStripeCustomer, createStripeIntent]);

  return { stripeClientSecret };
};

/**
 * @url https://friendliai.quip.com/4MoZAjWSGmEB/Stripe-Integration
 * @url https://docs.stripe.com/stripe-js/react
 */
export const useCheckStripeSetupIntentCompletion = () => {
  const { t } = useTranslation();
  const { teamId = '' } = useParams();

  const stripe = useStripe();
  const elements = useElements();

  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { cards } = usePaymentCards(teamId);
  const [checkStripeSetupIntentCompletion] = useMutation(
    TeamCheckStripeSetupIntentCompletionDocument,
    {
      refetchQueries: [
        { query: TeamPaymentCardsDocument, variables: { teamId } },
        { query: TeamPaymentDocument, variables: { teamId } },
      ],
      onCompleted: (data) => {
        const completion = data?.clientTeamCheckStripeSetupIntentCompletion?.state;

        if (!isNil(completion)) {
          if (
            completion === ClientTeamStripeSetupIntentState.Failed ||
            completion === ClientTeamStripeSetupIntentState.Unknown
          ) {
            setError(true);
          } else if (completion === ClientTeamStripeSetupIntentState.Succeeded) {
            const isFirstCreditCard = cards.length === 0;

            ampTrack.retention.ADD_CREDIT_CARD({ first: isFirstCreditCard });

            notifications.show({
              ...successProps,
              message: t('team_settings.payment_method_added'),
            });
            modals.closeAll();
          }
        }
      },
    }
  );

  const onCheckStripeSetupIntentCompletion = async (clientSecret: string | null) => {
    if (!clientSecret) throw new Error('Client secret is required');

    setLoading(true);

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      throw new Error('Stripe.js has not loaded.');
    }

    await elements.submit();

    await stripe.confirmSetup({
      clientSecret,
      elements,
      redirect: 'if_required',
    });

    await checkStripeSetupIntentCompletion({
      variables: { input: { clientSecret } },
    });

    setLoading(false);
  };

  return {
    error,
    loading,
    onCheckStripeSetupIntentCompletion,
  };
};
